// src/features/records/hooks/useRecordReaction.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getReactionAggregateCount,
  getReactionEnumerationCount,
  getReactionNodeSnapshot,
  subscribeToReactionMembership,
  toggleRecordReaction
} from '../services/interactions/reactions';

const DEFAULT_REACTION_KEY = 'e:2764';
const REACTION_COUNT_CACHE_TTL_MS = 15_000;
const AGGREGATION_RETRY_INTERVAL_MS = 5 * 60_000;
const AGGREGATION_ENUMERATION_INTERVAL_MS = 5 * 60_000;

const reactionCountCache = new Map();
const aggregationFailureCache = new Map();
const cacheSubscribers = new Map();
const cacheRefreshHandlers = new Map();
const cacheRevalidationTimers = new Map();

function getCacheKey(recordId, reactionKey) {
  if (!recordId || !reactionKey) return null;
  return `${recordId}::${reactionKey}`;
}

function notifyCacheSubscribers(cacheKey) {
  if (!cacheKey) {
    return;
  }

  const listeners = cacheSubscribers.get(cacheKey);
  if (!listeners || listeners.size === 0) {
    return;
  }

  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.error('Failed to notify reaction count subscriber:', err);
    }
  });
}

function subscribeToReactionCount(cacheKey, listener) {
  if (!cacheKey || typeof listener !== 'function') {
    return () => {};
  }

  let listeners = cacheSubscribers.get(cacheKey);
  if (!listeners) {
    listeners = new Set();
    cacheSubscribers.set(cacheKey, listeners);
  }

  listeners.add(listener);

  return () => {
    const currentListeners = cacheSubscribers.get(cacheKey);
    if (!currentListeners) {
      return;
    }

    currentListeners.delete(listener);

    if (currentListeners.size === 0) {
      cacheSubscribers.delete(cacheKey);
    }
  };
}

function clearCacheRevalidation(cacheKey) {
  if (!cacheKey) {
    return;
  }

  const timer = cacheRevalidationTimers.get(cacheKey);
  if (timer) {
    clearTimeout(timer);
    cacheRevalidationTimers.delete(cacheKey);
  }
}

function registerCacheRefreshHandler(cacheKey, handler) {
  if (!cacheKey || typeof handler !== 'function') {
    return () => {};
  }

  let handlers = cacheRefreshHandlers.get(cacheKey);
  if (!handlers) {
    handlers = new Set();
    cacheRefreshHandlers.set(cacheKey, handlers);
  }

  handlers.add(handler);

  return () => {
    const currentHandlers = cacheRefreshHandlers.get(cacheKey);
    if (!currentHandlers) {
      return;
    }

    currentHandlers.delete(handler);

    if (currentHandlers.size === 0) {
      cacheRefreshHandlers.delete(cacheKey);
    }
  };
}

function requestCacheRefresh(cacheKey) {
  if (!cacheKey) {
    return;
  }

  const handlers = cacheRefreshHandlers.get(cacheKey);
  if (!handlers || handlers.size === 0) {
    return;
  }

  for (const handler of Array.from(handlers)) {
    if (typeof handler !== 'function') {
      continue;
    }

    try {
      handler();
      break;
    } catch (err) {
      console.error('Failed to refresh reaction count cache:', err);
    }
  }
}

function scheduleCacheRevalidation(cacheKey) {
  if (!cacheKey) {
    return;
  }

  clearCacheRevalidation(cacheKey);

  const entry = reactionCountCache.get(cacheKey);
  if (!entry || !Number.isFinite(entry.fetchedAt)) {
    return;
  }

  const age = Date.now() - entry.fetchedAt;
  const delay = Math.max(0, REACTION_COUNT_CACHE_TTL_MS - age);

  const timer = setTimeout(() => {
    cacheRevalidationTimers.delete(cacheKey);
    requestCacheRefresh(cacheKey);
  }, delay);

  cacheRevalidationTimers.set(cacheKey, timer);
}

function normaliseCount(value) {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  return Math.floor(value);
}

function isInvalidAggregationPayload(error) {
  if (!error) return false;
  const code = typeof error.code === 'string' ? error.code : '';
  return code === 'invalid-argument' || code === 'failed-precondition';
}

function isCacheStale(entry) {
  if (!entry) return true;
  if (!Number.isFinite(entry.count)) return true;
  if (!Number.isFinite(entry.fetchedAt)) return true;
  return Date.now() - entry.fetchedAt > REACTION_COUNT_CACHE_TTL_MS;
}

/**
 * Manage reaction membership and counts for a record.
 *
 * Subscribes to the provided user's reactor document to keep `liked` in sync,
 * fetches reaction counts on demand, and provides an optimistic toggle helper
 * backed by the toggleRecordReaction callable.
 *
 * Pass `options.userId` to enable membership tracking and toggling.
 *
 * @param {string} recordId
 * @param {string} [reactionKey]
 * @param {{ initialCount?: number; initialLiked?: boolean; enabled?: boolean; userId?: string }} [options]
 */
export default function useRecordReaction(
  recordId,
  reactionKey = DEFAULT_REACTION_KEY,
  options = {}
) {
  const {
    initialCount = 0,
    initialLiked = false,
    enabled: enabledOption,
    userId: userIdOption,
  } = options || {};

  const enabled = Boolean(recordId && reactionKey && (enabledOption ?? true));

  const [count, setCount] = useState(() => normaliseCount(initialCount));
  const [liked, setLiked] = useState(() => Boolean(initialLiked));
  const [isToggling, setIsToggling] = useState(false);
  const [isFetchingCount, setIsFetchingCount] = useState(false);
  const [error, setError] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const currentUserId = userIdOption ?? null;
  const cacheKey = useMemo(() => getCacheKey(recordId, reactionKey), [recordId, reactionKey]);

  const isTogglingRef = useRef(false);
  const countRef = useRef(count);
  const refreshSeqRef = useRef(0);
  const lastInputsRef = useRef({
    recordId: recordId ?? null,
    initialCount: normaliseCount(initialCount),
    initialLiked: Boolean(initialLiked),
  });

  useEffect(() => {
    countRef.current = count;
  }, [count]);

  useEffect(() => {
    if (!enabled || !cacheKey) {
      return;
    }

    const updateFromCache = () => {
      const cached = reactionCountCache.get(cacheKey);
      if (cached && Number.isFinite(cached.count)) {
        setCount(normaliseCount(cached.count));
      }
    };

    updateFromCache();
    const unsubscribe = subscribeToReactionCount(cacheKey, updateFromCache);
    return unsubscribe;
  }, [cacheKey, enabled]);

  const applyCountToCache = useCallback(
    (nextCount) => {
      if (!cacheKey) {
        return;
      }

      const normalised = normaliseCount(nextCount);
      reactionCountCache.set(cacheKey, { count: normalised, fetchedAt: Date.now() });
      scheduleCacheRevalidation(cacheKey);
      notifyCacheSubscribers(cacheKey);
    },
    [cacheKey]
  );

  const applyAggregationFailure = useCallback(
    (updater) => {
      if (!cacheKey) return;
      const previous = aggregationFailureCache.get(cacheKey) ?? null;
      const nextValue = typeof updater === 'function' ? updater(previous) : updater;
      if (nextValue) {
        aggregationFailureCache.set(cacheKey, nextValue);
      } else {
        aggregationFailureCache.delete(cacheKey);
      }
    },
    [cacheKey]
  );

  useEffect(() => {
    const nextRecordId = recordId ?? null;
    const nextCount = normaliseCount(initialCount);
    const nextLiked = Boolean(initialLiked);
    const prev = lastInputsRef.current;

    const recordChanged = prev.recordId !== nextRecordId;
    const countChanged = prev.initialCount !== nextCount;
    const likedChanged = prev.initialLiked !== nextLiked;

    if (recordChanged || countChanged) {
      if (!cacheKey) {
        setCount(nextCount);
      } else {
        applyCountToCache(nextCount);
      }
      setRefreshNonce((value) => value + 1);
    }

    if (recordChanged || likedChanged) {
      setLiked(nextLiked);
    }

    if (recordChanged) {
      setError(null);
      applyAggregationFailure(null);
    }

    lastInputsRef.current = {
      recordId: nextRecordId,
      initialCount: nextCount,
      initialLiked: nextLiked,
    };
  }, [applyAggregationFailure, applyCountToCache, cacheKey, initialCount, initialLiked, recordId]);

  const refreshCount = useCallback(async () => {
    if (!enabled || !recordId || !reactionKey) {
      return normaliseCount(countRef.current);
    }

    setIsFetchingCount(true);
    const mySeq = ++refreshSeqRef.current;

    const commitCount = (value, { clearFailure = false, failureUpdater } = {}) => {
      const normalised = normaliseCount(value);

      if (mySeq === refreshSeqRef.current) {
        if (!isTogglingRef.current) {
          applyCountToCache(normalised);
        }

        if (clearFailure) {
          applyAggregationFailure(null);
        } else if (failureUpdater !== undefined) {
          applyAggregationFailure(failureUpdater);
        }

        setError(null);
      }

      return normalised;
    };

    const failureState = cacheKey ? aggregationFailureCache.get(cacheKey) ?? null : null;
    const now = Date.now();
    const canRetryAggregation =
      !failureState ||
      now - failureState.lastFailureAt >= AGGREGATION_RETRY_INTERVAL_MS ||
      !Number.isFinite(failureState.fallbackCount);

    try {
      if (canRetryAggregation) {
        const nextCount = await getReactionAggregateCount(recordId, reactionKey);
        return commitCount(nextCount, { clearFailure: true });
      }

      if (failureState && Number.isFinite(failureState.fallbackCount)) {
        return commitCount(failureState.fallbackCount);
      }
    } catch (err) {
      if (isInvalidAggregationPayload(err)) {
        try {
          const reactionNodeSnapshot = await getReactionNodeSnapshot(recordId, reactionKey);

          const shouldEnumerate =
            !failureState ||
            !Number.isFinite(failureState.fallbackCount) ||
            now - (failureState.lastEnumeratedAt ?? 0) >= AGGREGATION_ENUMERATION_INTERVAL_MS;

          if (!reactionNodeSnapshot.exists()) {
            const nextCount = 0;
            return commitCount(nextCount, {
              failureUpdater: {
                lastFailureAt: now,
                lastEnumeratedAt: now,
                fallbackCount: nextCount,
              },
            });
          }

          if (shouldEnumerate) {
            const fallbackCount = normaliseCount(
              await getReactionEnumerationCount(recordId, reactionKey)
            );

            return commitCount(fallbackCount, {
              failureUpdater: {
                lastFailureAt: now,
                lastEnumeratedAt: now,
                fallbackCount,
              },
            });
          }

          if (failureState && Number.isFinite(failureState.fallbackCount)) {
            const fallbackCount = normaliseCount(failureState.fallbackCount);
            return commitCount(fallbackCount, {
              failureUpdater: {
                lastFailureAt: now,
                lastEnumeratedAt: failureState.lastEnumeratedAt,
                fallbackCount,
              },
            });
          }
        } catch (fallbackError) {
          console.error(
            'Failed to reconcile reaction count after invalid aggregation payload:',
            fallbackError
          );
        }
      }

      console.error('Failed to fetch reaction count:', err);
      if (mySeq === refreshSeqRef.current) {
        setError(err);
        applyAggregationFailure((previous) => {
          if (!previous) {
            return {
              lastFailureAt: now,
              lastEnumeratedAt: 0,
              fallbackCount: null,
            };
          }

          return {
            ...previous,
            lastFailureAt: now,
          };
        });
      }
      throw err;
    } finally {
      if (mySeq === refreshSeqRef.current) {
        setIsFetchingCount(false);
      }
    }
  }, [applyAggregationFailure, applyCountToCache, cacheKey, enabled, recordId, reactionKey]);

  const handleRefreshRequest = useCallback(() => {
    refreshCount().catch(() => {
      // Errors are already surfaced inside refreshCount.
    });
  }, [refreshCount]);

  useEffect(() => {
    if (!enabled || !cacheKey) {
      return;
    }

    return registerCacheRefreshHandler(cacheKey, handleRefreshRequest);
  }, [cacheKey, enabled, handleRefreshRequest]);

  useEffect(() => {
    if (!enabled || !cacheKey) {
      return;
    }

    const cached = reactionCountCache.get(cacheKey);
    if (cached && Number.isFinite(cached.count)) {
      setCount(normaliseCount(cached.count));
    }

    if (!cached || isCacheStale(cached)) {
      let cancelled = false;

      (async () => {
        try {
          if (!cancelled) {
            await refreshCount();
          }
        } catch {
          // Errors are handled inside refreshCount.
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    return undefined;
  }, [cacheKey, enabled, refreshCount]);

  useEffect(() => {
    if (!enabled || !cacheKey) {
      return;
    }

    if (refreshNonce === 0) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        if (!cancelled) {
          await refreshCount();
        }
      } catch {
        // Errors are handled inside refreshCount.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cacheKey, enabled, refreshCount, refreshNonce]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!currentUserId) {
      setLiked(false);
      return;
    }

    const unsubscribe = subscribeToReactionMembership(
      { recordId, reactionKey, userId: currentUserId },
      (exists) => {
        setLiked(exists);
      },
      (err) => {
        console.error('Failed to subscribe to reaction membership:', err);
        setError(err);
      }
    );

    return unsubscribe;
  }, [currentUserId, enabled, recordId, reactionKey]);

  const toggle = useCallback(async () => {
    if (!enabled) {
      return {
        recordId,
        reactionKey,
        liked,
        count,
      };
    }

    if (!recordId || !reactionKey) {
      const err = new Error('Record ID and reaction key are required to toggle reactions');
      setError(err);
      throw err;
    }

    if (!currentUserId) {
      const err = new Error('User must be authenticated to toggle reactions');
      setError(err);
      throw err;
    }

    if (isTogglingRef.current) {
      return {
        recordId,
        reactionKey,
        liked,
        count,
      };
    }

    const previousState = { liked, count };
    const optimisticLiked = !liked;
    const optimisticCount = normaliseCount(previousState.count + (optimisticLiked ? 1 : -1));

    isTogglingRef.current = true;
    setIsToggling(true);
    setError(null);
    setLiked(optimisticLiked);
    setCount(optimisticCount);

    try {
      const result = await toggleRecordReaction(recordId, reactionKey);
      const nextCount = normaliseCount(result?.count);

      setLiked(Boolean(result?.liked));
      applyCountToCache(nextCount);
      applyAggregationFailure(null);
      return {
        recordId: result?.recordId ?? recordId,
        reactionKey: result?.reactionKey ?? reactionKey,
        liked: Boolean(result?.liked),
        count: nextCount,
      };
    } catch (err) {
      console.error('Failed to toggle reaction:', err);
      setLiked(previousState.liked);
      setCount(previousState.count);
      setError(err);
      if (cacheKey) {
        reactionCountCache.delete(cacheKey);
        clearCacheRevalidation(cacheKey);
        notifyCacheSubscribers(cacheKey);
      }
      throw err;
    } finally {
      isTogglingRef.current = false;
      setIsToggling(false);
    }
  }, [
    applyCountToCache,
    applyAggregationFailure,
    cacheKey,
    count,
    currentUserId,
    enabled,
    liked,
    reactionKey,
    recordId,
  ]);

  return {
    liked,
    count,
    isToggling,
    isFetchingCount,
    error,
    toggle,
    refresh: refreshCount,
    currentUserId,
  };
}

export const __TEST__ = {
  REACTION_COUNT_CACHE_TTL_MS,
  AGGREGATION_RETRY_INTERVAL_MS,
  AGGREGATION_ENUMERATION_INTERVAL_MS,
  reactionCountCache,
  aggregationFailureCache,
  cacheSubscribers,
  cacheRefreshHandlers,
  cacheRevalidationTimers,
  scheduleCacheRevalidation,
  isInvalidAggregationPayload,
};
